import React, { useState, useRef, useEffect } from 'react';

const VideoSyncTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [currentTest, setCurrentTest] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const testRef = useRef(null);

  // Mock telemetry data for testing (more comprehensive)
  const mockTelemetryData = [
    { time: 0, lat: 40.7128, lon: -74.0060, elevation: 100, front_brake: 0, rear_brake: 0, speed: 0 },
    { time: 5, lat: 40.7129, lon: -74.0059, elevation: 102, front_brake: 0, rear_brake: 0, speed: 15 },
    { time: 10, lat: 40.7130, lon: -74.0058, elevation: 105, front_brake: 0.2, rear_brake: 0.1, speed: 25 },
    { time: 15, lat: 40.7131, lon: -74.0057, elevation: 108, front_brake: 0.3, rear_brake: 0.2, speed: 30 },
    { time: 20, lat: 40.7132, lon: -74.0056, elevation: 110, front_brake: 0.5, rear_brake: 0.3, speed: 35 },
    { time: 25, lat: 40.7133, lon: -74.0055, elevation: 112, front_brake: 0.7, rear_brake: 0.4, speed: 40 },
    { time: 30, lat: 40.7134, lon: -74.0054, elevation: 115, front_brake: 0.8, rear_brake: 0.6, speed: 45 },
    { time: 35, lat: 40.7135, lon: -74.0053, elevation: 118, front_brake: 0.9, rear_brake: 0.7, speed: 50 },
    { time: 40, lat: 40.7136, lon: -74.0052, elevation: 120, front_brake: 1.0, rear_brake: 0.8, speed: 55 },
    { time: 45, lat: 40.7137, lon: -74.0051, elevation: 122, front_brake: 0.8, rear_brake: 0.6, speed: 50 },
    { time: 50, lat: 40.7138, lon: -74.0050, elevation: 125, front_brake: 0.6, rear_brake: 0.4, speed: 45 },
    { time: 55, lat: 40.7139, lon: -74.0049, elevation: 128, front_brake: 0.4, rear_brake: 0.3, speed: 40 },
    { time: 60, lat: 40.7140, lon: -74.0048, elevation: 130, front_brake: 0.3, rear_brake: 0.2, speed: 35 },
    { time: 65, lat: 40.7141, lon: -74.0047, elevation: 132, front_brake: 0.2, rear_brake: 0.1, speed: 30 },
    { time: 70, lat: 40.7142, lon: -74.0046, elevation: 135, front_brake: 0.1, rear_brake: 0.0, speed: 25 },
    { time: 75, lat: 40.7143, lon: -74.0045, elevation: 138, front_brake: 0.0, rear_brake: 0.0, speed: 20 },
    { time: 80, lat: 40.7144, lon: -74.0044, elevation: 140, front_brake: 0.0, rear_brake: 0.0, speed: 15 },
    { time: 85, lat: 40.7145, lon: -74.0043, elevation: 142, front_brake: 0.0, rear_brake: 0.0, speed: 10 },
    { time: 90, lat: 40.7146, lon: -74.0042, elevation: 145, front_brake: 0.0, rear_brake: 0.0, speed: 5 },
    { time: 95, lat: 40.7147, lon: -74.0041, elevation: 148, front_brake: 0.0, rear_brake: 0.0, speed: 0 },
  ];

  const testCases = [
    {
      id: 'map-click',
      name: 'Map Click Synchronization',
      description: 'Click on map should sync video, graph, and timeline',
      test: async () => {
        // Simulate map click at time 30
        const mapElement = document.querySelector('.leaflet-container');
        if (!mapElement) throw new Error('Map not found');
        
        // Trigger map click event
        const clickEvent = new MouseEvent('click', {
          clientX: 400,
          clientY: 300,
          bubbles: true
        });
        mapElement.dispatchEvent(clickEvent);
        
        // Wait for synchronization
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if video time is approximately 30 seconds
        const videoElement = document.querySelector('video');
        if (videoElement) {
          const videoTime = videoElement.currentTime;
          if (Math.abs(videoTime - 30) > 2) {
            throw new Error(`Video time ${videoTime}s doesn't match expected 30s`);
          }
        }
        
        return 'Map click synchronized video to 30s';
      }
    },
    {
      id: 'graph-click',
      name: 'Graph Click Synchronization',
      description: 'Click on graph should sync video, map, and timeline',
      test: async () => {
        // Find the chart canvas
        const chartCanvas = document.querySelector('canvas');
        if (!chartCanvas) throw new Error('Chart canvas not found');
        
        // Simulate click at 50% of the chart (should be around 45s)
        const rect = chartCanvas.getBoundingClientRect();
        const clickEvent = new MouseEvent('click', {
          clientX: rect.left + rect.width * 0.5,
          clientY: rect.top + rect.height * 0.5,
          bubbles: true
        });
        chartCanvas.dispatchEvent(clickEvent);
        
        // Wait for synchronization
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if video time is approximately 45 seconds
        const videoElement = document.querySelector('video');
        if (videoElement) {
          const videoTime = videoElement.currentTime;
          if (Math.abs(videoTime - 45) > 2) {
            throw new Error(`Video time ${videoTime}s doesn't match expected 45s`);
          }
        }
        
        return 'Graph click synchronized video to 45s';
      }
    },
    {
      id: 'timeline-click',
      name: 'Timeline Click Synchronization',
      description: 'Click on timeline should sync video, map, and graph',
      test: async () => {
        // Find the timeline element
        const timelineElement = document.querySelector('[data-testid="video-timeline"]') || 
                               document.querySelector('.video-timeline') ||
                               document.querySelector('input[type="range"]');
        
        if (!timelineElement) throw new Error('Timeline not found');
        
        // Simulate click at 70% of the timeline
        const rect = timelineElement.getBoundingClientRect();
        const clickEvent = new MouseEvent('click', {
          clientX: rect.left + rect.width * 0.7,
          clientY: rect.top + rect.height * 0.5,
          bubbles: true
        });
        timelineElement.dispatchEvent(clickEvent);
        
        // Wait for synchronization
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if video time is approximately 63 seconds (70% of 90s)
        const videoElement = document.querySelector('video');
        if (videoElement) {
          const videoTime = videoElement.currentTime;
          if (Math.abs(videoTime - 63) > 2) {
            throw new Error(`Video time ${videoTime}s doesn't match expected 63s`);
          }
        }
        
        return 'Timeline click synchronized video to 63s';
      }
    },
    {
      id: 'video-play-pause',
      name: 'Video Play/Pause Controls',
      description: 'Video play/pause should work correctly',
      test: async () => {
        const videoElement = document.querySelector('video');
        if (!videoElement) throw new Error('Video element not found');
        
        // Test play
        videoElement.play();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (videoElement.paused) {
          throw new Error('Video should be playing but is paused');
        }
        
        // Test pause
        videoElement.pause();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!videoElement.paused) {
          throw new Error('Video should be paused but is playing');
        }
        
        return 'Video play/pause controls working correctly';
      }
    },
    {
      id: 'video-seek',
      name: 'Video Seek Functionality',
      description: 'Video seeking should work correctly',
      test: async () => {
        const videoElement = document.querySelector('video');
        if (!videoElement) throw new Error('Video element not found');
        
        // Seek to 30 seconds
        videoElement.currentTime = 30;
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (Math.abs(videoElement.currentTime - 30) > 1) {
          throw new Error(`Video seek failed. Expected 30s, got ${videoElement.currentTime}s`);
        }
        
        return 'Video seek functionality working correctly';
      }
    },
    {
      id: 'youtube-player',
      name: 'YouTube Player Integration',
      description: 'YouTube player should work with synchronization',
      test: async () => {
        // Check if YouTube player is loaded
        const youtubePlayer = document.querySelector('iframe[src*="youtube"]');
        if (!youtubePlayer) {
          return 'No YouTube player found (test skipped)';
        }
        
        // Wait for YouTube player to be ready
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Check if YouTube API is available
        if (!window.YT || !window.YT.Player) {
          throw new Error('YouTube API not loaded');
        }
        
        return 'YouTube player integration working correctly';
      }
    },
    {
      id: 'multiple-videos',
      name: 'Multiple Video Synchronization',
      description: 'Multiple videos should sync together',
      test: async () => {
        // This test would require multiple video players
        // For now, just check if the sync manager is working
        const syncManager = window.videoSyncManager;
        if (!syncManager) {
          throw new Error('Video sync manager not found');
        }
        
        return 'Multiple video sync manager available';
      }
    },
    {
      id: 'edge-cases',
      name: 'Edge Cases',
      description: 'Test edge cases like seeking to start/end',
      test: async () => {
        const videoElement = document.querySelector('video');
        if (!videoElement) throw new Error('Video element not found');
        
        // Test seeking to start
        videoElement.currentTime = 0;
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (videoElement.currentTime > 1) {
          throw new Error('Seeking to start failed');
        }
        
        // Test seeking to end
        videoElement.currentTime = videoElement.duration - 1;
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (videoElement.currentTime < videoElement.duration - 2) {
          throw new Error('Seeking to end failed');
        }
        
        return 'Edge cases handled correctly';
      }
    },
    {
      id: 'rapid-seek',
      name: 'Rapid Seek Test',
      description: 'Test rapid seeking between different time points',
      test: async () => {
        const videoElement = document.querySelector('video');
        if (!videoElement) throw new Error('Video element not found');
        
        // Rapid seek test
        const seekTimes = [0, 30, 60, 90, 45, 15, 75];
        for (const time of seekTimes) {
          videoElement.currentTime = time;
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (Math.abs(videoElement.currentTime - time) > 2) {
            throw new Error(`Rapid seek failed at time ${time}s, got ${videoElement.currentTime}s`);
          }
        }
        
        return 'Rapid seeking working correctly';
      }
    },
    {
      id: 'sync-accuracy',
      name: 'Sync Accuracy Test',
      description: 'Test synchronization accuracy between components',
      test: async () => {
        // This test would check if all components are in sync
        // For now, just verify that the sync manager is working
        const syncManager = window.videoSyncManager;
        if (!syncManager) {
          throw new Error('Video sync manager not found');
        }
        
        // Check if we have registered video players
        const registeredPlayers = Object.keys(syncManager.videoPlayers || {});
        if (registeredPlayers.length === 0) {
          throw new Error('No video players registered with sync manager');
        }
        
        return `Sync manager working with ${registeredPlayers.length} registered players`;
      }
    },
    {
      id: 'error-handling',
      name: 'Error Handling Test',
      description: 'Test error handling for invalid operations',
      test: async () => {
        const videoElement = document.querySelector('video');
        if (!videoElement) throw new Error('Video element not found');
        
        // Test seeking to invalid time
        const originalTime = videoElement.currentTime;
        videoElement.currentTime = -10; // Invalid time
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Should handle gracefully (either clamp to 0 or stay at original)
        if (videoElement.currentTime < 0) {
          throw new Error('Invalid seek time not handled properly');
        }
        
        // Test seeking beyond duration
        videoElement.currentTime = videoElement.duration + 100;
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Should handle gracefully (either clamp to duration or stay at original)
        if (videoElement.currentTime > videoElement.duration + 1) {
          throw new Error('Seek beyond duration not handled properly');
        }
        
        return 'Error handling working correctly';
      }
    }
  ];

  const runTest = async (testCase) => {
    setCurrentTest(testCase);
    try {
      const result = await testCase.test();
      setTestResults(prev => [...prev, {
        id: testCase.id,
        name: testCase.name,
        status: 'passed',
        result: result,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      setTestResults(prev => [...prev, {
        id: testCase.id,
        name: testCase.name,
        status: 'failed',
        error: error.message,
        timestamp: new Date().toISOString()
      }]);
    }
    setCurrentTest(null);
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    for (const testCase of testCases) {
      await runTest(testCase);
      // Wait between tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    setIsRunning(false);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const downloadSampleData = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      "time,lat,lon,elevation,front_brake,rear_brake,speed\n" +
      mockTelemetryData.map(row => 
        `${row.time},${row.lat},${row.lon},${row.elevation},${row.front_brake},${row.rear_brake},${row.speed}`
      ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sample-telemetry.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Video Synchronization Test Suite</h1>
      
      <div style={{ 
        backgroundColor: '#e3f2fd', 
        border: '1px solid #2196f3', 
        borderRadius: '4px', 
        padding: '15px', 
        marginBottom: '20px' 
      }}>
        <h3>Instructions:</h3>
        <ol>
          <li>Click "Download Sample Data" to get a sample telemetry CSV file</li>
          <li>Upload the sample data to the dashboard using the file input</li>
          <li>Click "Run All Tests" to test all synchronization features</li>
          <li>Or run individual tests to debug specific issues</li>
          <li>Check the test results to see what's working and what needs fixing</li>
        </ol>
        <p><strong>Note:</strong> This test suite will help identify and fix video synchronization issues systematically.</p>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runAllTests} 
          disabled={isRunning}
          style={{
            padding: '10px 20px',
            marginRight: '10px',
            backgroundColor: isRunning ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunning ? 'not-allowed' : 'pointer'
          }}
        >
          {isRunning ? 'Running Tests...' : 'Run All Tests'}
        </button>
        
        <button 
          onClick={clearResults}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Clear Results
        </button>
        
        <button 
          onClick={downloadSampleData}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Download Sample Data
        </button>
      </div>

      {currentTest && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#e3f2fd', 
          border: '1px solid #2196f3',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Running:</strong> {currentTest.name}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Test Dashboard */}
        <div>
          <h2>Test Dashboard</h2>
          <div style={{ 
            padding: '20px', 
            border: '2px dashed #ccc', 
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            textAlign: 'center'
          }}>
            <p>Dashboard component temporarily disabled for testing.</p>
            <p>Use the sample data download to test with the main dashboard.</p>
            <p>Upload the CSV file to the main dashboard at <a href="/dashboard">/dashboard</a></p>
          </div>
        </div>

        {/* Test Results */}
        <div>
          <h2>Test Results</h2>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {testResults.length === 0 ? (
              <p>No tests run yet. Click "Run All Tests" to start.</p>
            ) : (
              testResults.map((result, index) => (
                <div
                  key={index}
                  style={{
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: result.status === 'passed' ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${result.status === 'passed' ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: '4px'
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {result.status === 'passed' ? '✅' : '❌'} {result.name}
                  </div>
                  {result.status === 'passed' ? (
                    <div style={{ color: '#155724' }}>{result.result}</div>
                  ) : (
                    <div style={{ color: '#721c24' }}>Error: {result.error}</div>
                  )}
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {new Date(result.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Test Cases List */}
      <div style={{ marginTop: '20px' }}>
        <h2>Available Test Cases</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
          {testCases.map((testCase) => (
            <div
              key={testCase.id}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                {testCase.name}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                {testCase.description}
              </div>
              <button
                onClick={() => runTest(testCase)}
                disabled={isRunning}
                style={{
                  padding: '5px 10px',
                  backgroundColor: isRunning ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isRunning ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                Run Test
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoSyncTest;
